import { z } from 'zod/v4';

import { ActionPermission } from '../../backend-generated-types';
import { HttpBase } from './httpBase';
import { HttpEndpoint } from './HttpEndpoint';

export type TQueryParamValue = string | number | boolean | string[];

export type ArrayFormat = 'comma' | 'repeat';

export type ActionPermissionFn<Args extends any[] = []> = (...args: Args) => ActionPermission | undefined;
interface EndpointProps<
    TPathParams extends z.ZodTypeAny,
    TQueryParams extends z.ZodTypeAny,
    THttpResponseBody extends z.ZodTypeAny,
    THttpRequestBody extends z.ZodTypeAny,
    TActionArgs extends any[] = [],
> {
    /**
     * Provide a human-readable description for the endpoint.
     */
    description: string;

    /**
     * Describes the path parameters of the endpoint.
     */
    pathParams: TPathParams;

    /**
     * Describes the query parameters of the endpoint
     */
    queryParams: TQueryParams;

    requestBody: THttpRequestBody;
    responseBody: THttpResponseBody;
    handleResponse?: (response: Response, registeredEndpoint: string) => Promise<z.infer<THttpResponseBody>>;

    invalidates?: HttpEndpoint[];

    /** Endpoints to remove after the mutation, typically for DELETE endpoints. */
    removes?: HttpEndpoint[];

    /**
     * Array of action permissions that will be used to return checkPermission.
     * If ANY permission is satisfied, checkPermission will return true
     */
    actionPermission?: ActionPermissionFn<TActionArgs>;

    /** Defaults to `'api'`. */
    base?: HttpBase;

    /**
     * How to serialize array query parameters.
     * - `'comma'`: join values with commas (`key=a,b,c`) — used by Rust/Actix backends
     * - `'repeat'`: repeat the key for each value (`key=a&key=b&key=c`) — used by Scala/Play backends
     *
     * @default 'comma'
     */
    arrayFormat?: ArrayFormat;
}

export type EndpointDefinition<
    TPathParams extends z.ZodTypeAny,
    TQueryParams extends z.ZodTypeAny,
    THttpResponseBody extends z.ZodTypeAny,
    THttpRequestBody extends z.ZodTypeAny,
    TActionArgs extends any[] = [],
> = {
    description: string;
    pathParams: TPathParams;
    queryParams: TQueryParams;
    requestBody: THttpRequestBody;
    responseBody: THttpResponseBody;
    handleResponse: EndpointProps<TPathParams, TQueryParams, THttpResponseBody, THttpRequestBody>['handleResponse'];
    invalidates: HttpEndpoint[];
    removes: HttpEndpoint[];
    actionPermission: ActionPermissionFn<TActionArgs>;
    base: HttpBase | undefined;
    arrayFormat: ArrayFormat;
};

/**
 * Utility function that defines an endpoint. As you can see from the implementation,
 * this function doesn't do anything - it exists merely to enforce the definition of types.
 *
 * The return type is named so declaration files write `EndpointDefinition<…>` instead of spelling out the response
 * type again for every endpoint, which makes some of them too long for tsc to write.
 */
export function endpoint<
    TPathParams extends z.ZodTypeAny,
    TQueryParams extends z.ZodTypeAny,
    THttpRequestBody extends z.ZodTypeAny,
    THttpResponseBody extends z.ZodTypeAny,
    TActionArgs extends any[] = [],
>({
    description,
    pathParams,
    queryParams,
    requestBody,
    responseBody,
    handleResponse,
    invalidates = [],
    removes = [],
    actionPermission = () => undefined,
    base,
    arrayFormat = 'comma',
}: EndpointProps<TPathParams, TQueryParams, THttpRequestBody, THttpResponseBody, TActionArgs>): EndpointDefinition<
    TPathParams,
    TQueryParams,
    THttpRequestBody,
    THttpResponseBody,
    TActionArgs
> {
    return {
        description,
        pathParams,
        queryParams,
        requestBody,
        responseBody,
        handleResponse,
        invalidates,
        removes,
        actionPermission,
        base,
        arrayFormat,
    };
}
